'use client';
import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import type {
  Exhibition,
  LandscapeDisplay,
  LandscapeImmersive,
  LandscapeDescription,
  LandscapeChapterHero,
  LandscapeChapter,
  LandscapeProcess,
  LandscapeSketchbook,
  LandscapeVisionDisplay,
  LandscapeDocumentary,
  LandscapeRenders,
  LandscapePress,
  LandscapeGallery,
  LandscapeExhibition,
  LandscapeEmbed
} from '@/types';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { Common_HeroSection } from '../landscape/CommonHeroSection';
import { Landscape_Documentary } from '../landscape/LandscapeDocumentary';
import { rowColumns, rowFrame } from '../landscape/documentary-row';
import { Landscape_Renders } from '../landscape/LandscapeRender';
import { Landscape_Gallery } from '../landscape/LandscapeGallery';
import { Landscape_Press } from '../landscape/LandscapePress';
import { getVideoOrFileData } from '@/lib/utils/media.utils';
import { EntryInformation, EntryPhotos, readLayout, type EntrySpec } from '../landscape/EntryInformation';
import { lexicalStateToHtml, hasRichTextContent } from '@/lib/utils/lexical-to-html';
import { Landscape_Exhibition } from '../landscape/LandscapeExhibition';
import { Landscape_Embed } from '../landscape/LandscapeEmbed';

interface ExhibitionProps {
  detail: Exhibition;
}

/** Runs of documentary blocks, so films that follow one another can share a row. */
function groupDocumentaries<T extends { blockType: string }>(layout: T[]): T[][] {
  const groups: T[][] = [];
  for (const block of layout) {
    const last = groups[groups.length - 1];
    if (block.blockType === 'LandscapeDocumentary' && last && last[0].blockType === 'LandscapeDocumentary') last.push(block);
    else groups.push([block]);
  }
  return groups;
}

export default function ExhibitionDetailPage({ detail }: ExhibitionProps) {
  // Etiketleri dönüştür
  const tags: string[] = [];

  // Etiketleri ekle
  if (detail.tags && Array.isArray(detail.tags)) {
    detail.tags.forEach(tag => {
      if (typeof tag === 'number') {
        tags.push(String(tag));
      } else if (tag && typeof tag === 'object' && 'title' in tag) {
        tags.push(tag.title);
      }
    });
  }

  // What the entry knows about itself. Shown by the description block where the layout has
  // one, and by the page itself where it does not, so no entry is left as a bare title.
  const specs: EntrySpec[] = [];
  if (detail.event) {
    specs.push({
      label: 'Event',
      specifications: [{ value: detail.event }],
    });
  }
  if (detail.format) {
    specs.push({
      label: 'Format',
      specifications: [{ value: detail.format }],
    });
  }


  // status
  if (detail.status && typeof detail.status === 'object') {
    specs.push({
      label: 'Status',
      specifications: [{ value: detail.status.title }],
    });
  }
  // type
  if (detail.type && typeof detail.type === 'object') {
    specs.push({
      label: 'Type',
      specifications: [{ value: detail.type.title }],
    });
  }
  // curator
  if (detail.curator) {
    specs.push({
      label: 'Curator',
      specifications: [{ value: detail.curator }],
    });
  }
  // gallery
  if (detail.gallery) {
    specs.push({
      label: 'Gallery',
      specifications: [{ value: detail.gallery }],
    });
  }
  // location
  if (detail.location) {
    specs.push({
      label: 'Location',
      specifications: [{ value: detail.location }],
    });
  }
  // date dd/mm/yyyy
  if (detail.duration?.startDate) {
    const day = (value: string) => new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const from = day(detail.duration.startDate);
    const to = detail.duration.endDate ? day(detail.duration.endDate) : null;
    specs.push({
      label: 'Dates',
      specifications: [{ value: to && to !== from ? `${from} – ${to}` : from }],
    });
  }

  if (detail.virtualTourUrl) {
    specs.push({
      label: 'Virtual Tour',
      specifications: [
        {
          value: new URL(detail.virtualTourUrl).hostname.replace(/^www\./, ''),
          url: detail.virtualTourUrl,
          light: true,
        },
      ],
    });
  }
  const pdfFile = getVideoOrFileData(detail.pressKitfile);
  if (pdfFile.url) {
    specs.push({
      label: 'Press Kit',
      specifications: [
        {
          value: 'Download Press Kit',
          url: pdfFile.url,
          light: true,
        },
      ],
    });
  }

  // medium
  // if (detail.medium) {
  //   specs.push({
  //     label: 'Medium',
  //     specifications: [{ value: detail.medium }],
  //   });
  // }
  // // dimensions
  // if (detail.dimensions) {
  //   specs.push({
  //     label: 'Dimensions',
  //     specifications: [{ value: detail.dimensions }],
  //   });
  // }
  // // location
  // if (detail.location) {
  //   specs.push({
  //     label: 'Location',
  //     specifications: [{ value: detail.location }],
  //   });
  // }

  // // materials array
  // if (detail.materials && Array.isArray(detail.materials)) {
  //   specs.push({
  //     label: 'Materials',
  //     specifications: detail.materials.map(material => ({
  //       value: material.material,
  //     })),
  //   });
  // }
  // // shippingDetails
  // if (detail.shippingDetails) {
  //   if (detail.shippingDetails.availability) {
  //     specs.push({
  //       label: 'Availability',
  //       specifications: [{ value: detail.shippingDetails.availability }],
  //     });
  //   }
  //   if (detail.shippingDetails.insurance) {
  //     specs.push({
  //       label: 'Insurance',
  //       specifications: [{ value: detail.shippingDetails.insurance }],
  //     });
  //   }
  // }
  // // year
  // if (detail.year) {
  //   specs.push({
  //     label: 'Year',
  //     specifications: [{ value: detail.year, light: true }],
  //   });
  // }
  // // collection
  // if (detail.collection) {
  //   if (typeof detail.collection !== 'number') {
  //     specs.push({
  //       label: 'Collection',
  //       specifications: [
  //         { value: detail.collection.name, url: `/catalog/collections/${detail.collection.slug}`, light: true },
  //       ],
  //     });
  //   }
  // }
  // // artwork
  // if (detail.artwork) {
  //   const artworks = [];
  //   for (const artwork of detail.artwork) {
  //     if (typeof artwork !== 'number') {
  //       artworks.push({ value: artwork.name, url: `/catalog/artworks/${artwork.id}`, light: true });
  //     }
  //   }
  //   if (artworks.length > 0) {
  //     specs.push({
  //       label: 'Artwork' + (artworks.length > 1 ? 's' : ''),
  //       specifications: artworks,
  //     });
  //   }
  // }

  // if (detail.type) {
  //   specs.push({
  //     label: 'Type',
  //     specifications: [{ value: detail.type }],
  //   });
  // }
  // // typeof detail.status
  // if (typeof detail.status === 'object') {
  //   specs.push({
  //     label: 'Status',
  //     specifications: [{ value: detail.status.title }],
  //   });
  // }
  // // date dd/mm/yyyy
  // if (detail.date) {
  //   const date = new Date(detail.date);
  //   const day = date.getDate();
  //   const month = date.getMonth() + 1;
  //   const year = date.getFullYear();
  //   specs.push({
  //     label: 'Date',
  //     specifications: [{ value: `${day}/${month}/${year}` }],
  //   });
  // }

  // if (detail.highlights) {
  //   specs.push({
  //     label: 'Highlights',
  //     specifications: detail.highlights.map(highlight => ({ value: highlight.highlight })),
  //   });
  // }

  // if (detail.virtualTourUrl) {
  //   specs.push({
  //     label: 'Project URL',
  //     specifications: [
  //       {
  //         value: new URL(detail.projectUrl).hostname.replace(/^www\./, ''),
  //         url: detail.projectUrl,
  //         light: true,
  //       },
  //     ],
  //   });
  // }
  // // partners
  // if (detail.partners) {
  //   specs.push({
  //     label: 'Partners',
  //     specifications: detail.partners.map(partner => ({ value: `${partner.name} - ${partner.role}` })),
  //   });
  // }
  if (detail.collaborator) {
    specs.push({
      label: 'Organised with',
      specifications: [{ value: detail.collaborator }],
    });
  }

  const typeTitle = detail.type && typeof detail.type === 'object' ? detail.type.title : null;
  const statusTitle = detail.status && typeof detail.status === 'object' ? detail.status.title : null;
  const place = [detail.gallery, detail.city, detail.country].filter(Boolean).join(', ');
  const startYear = detail.duration?.startDate ? new Date(detail.duration.startDate).getFullYear() : null;
  const endYear = detail.duration?.endDate ? new Date(detail.duration.endDate).getFullYear() : null;
  const years = startYear ? (endYear && endYear !== startYear ? `${startYear} – ${endYear}` : String(startYear)) : '';
  const page = readLayout(detail.layout);

  const GetLayout = ({
    layout,
  }: {
    layout:
      | LandscapeDisplay
      | LandscapeImmersive
      | LandscapeVisionDisplay
      | LandscapeDescription
      | LandscapeChapterHero
      | LandscapeChapter
      | LandscapeProcess
      | LandscapeSketchbook
      | LandscapeDocumentary
      | LandscapeRenders
      | LandscapeGallery
      | LandscapePress
      | LandscapeExhibition
      | LandscapeEmbed;
  }) => {
    switch (layout.blockType) {
      case 'LandscapeDisplay':
        return <Landscape_Display layout={layout} />;
      case 'LandscapeImmersive':
        return <Landscape_Immersive layout={layout} />;
      case 'LandscapeChapterHero':
        return <Landscape_ChapterHero layout={layout} />;
      case 'LandscapeChapter':
        return <Landscape_Chapter layout={layout} />;
      case 'LandscapeProcess':
        return <Landscape_Process layout={layout} />;
      case 'LandscapeSketchbook':
        return <Landscape_Sketchbook layout={layout} />;
      case 'LandscapeDocumentary':
        return <Landscape_Documentary layout={layout} />;
      case 'LandscapeRenders':
        return <Landscape_Renders layout={layout} />;
      case 'LandscapePress':
        return <Landscape_Press layout={layout} />;
      case 'LandscapeGallery':
        return <Landscape_Gallery layout={layout} />;
      case 'LandscapeExhibition':
        return <Landscape_Exhibition layout={layout} />;
      case 'LandscapeEmbed':
        return <Landscape_Embed layout={layout} />;
      case 'LandscapeDescription':
        return <Landscape_Description layout={layout} specs={specs} />;
      default:
        return null; // Eşleşme yoksa null döndür
    }
  };

  // Content area - ensure overflow works with parent container
  return (
    <div
      className="w-full bg-black relative h-full overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
      id="landscape-content"
    >
      {/* Hero Section */}
      <Common_HeroSection
        title={detail.title}
        description={detail.description}
        leftTop={[typeTitle, detail.format, statusTitle].filter(Boolean).join(' · ') || detail.gallery || ''}
        rightTop={years || detail.location || ''}
        subtitle={[detail.event, place].filter(Boolean).join(' — ')}
        tags={tags}
      />

      {/* An entry with no description block still shows what it knows. */}
      {page.describes ? null : <EntryInformation specs={specs} />}

      {/* About Section */}
      {hasRichTextContent(detail.about) && (
        <div className="content-section rounded-md mb-[12px]">
          <div className="pt-[15px] w-full px-[15px] rounded-md">
            <div className="font-mono text-[10px] pt-2 pb-12">
              <p className="text-[10px] font-mono text-gray-900 mb-4">[ {detail.aboutTitle?.trim() ? detail.aboutTitle.toUpperCase() : 'ABOUT'} ]</p>
              <div
                className="space-y-4 text-[10px] leading-relaxed text-gray-500 [&>p]:mb-3"
                dangerouslySetInnerHTML={{ __html: lexicalStateToHtml(detail.about) }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sections. Films that follow one another sit side by side, each in a card of its own. */}
      {groupDocumentaries(detail.layout ?? []).map((group, index) =>
        group.length > 1 ? (
          <div key={index} className={`grid grid-cols-1 ${rowColumns(group.length)} gap-x-[12px]`}>
            {group.map((lay, i) => (
              <Landscape_Documentary key={i} layout={lay as LandscapeDocumentary} inRow frame={rowFrame(group as LandscapeDocumentary[])} />
            ))}
          </div>
        ) : (
          <GetLayout key={index} layout={group[0]} />
        ),
      )}

      {/* The entry's own photographs, where the page shows no pictures or film of its own. */}
      {page.showsMedia ? null : <EntryPhotos photos={detail.photos} title={detail.title} />}
    </div>
  );
}
